import axios from 'axios'
import {
  ProgramPurseListSchema,
  ProgramPurseResourceSchema,
  GetPurseDistributionsResponseSchema,
  ProgramPurseAffiliationsResponseSchema,
  RunPreviewDistributionResponseSchema,
  RunPreviewDistributionResponse,
} from '@/queries/program_purses/schemas.ts'
import { parseRequestError } from '@/app/common/utilities.ts'

export type UpdatePurseAcademicTermTypeParams = {
  programId: number
  purseId: number
  academicTermTypeId: number
}

export const updatePurseAcademicTermType = async ({
  programId,
  purseId,
  academicTermTypeId,
}: UpdatePurseAcademicTermTypeParams) => {
  try {
    await axios.put(`/programs/${programId}/purses/${purseId}`, {
      academic_term_type_id: academicTermTypeId,
    })
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type GetProgramPursesParams = {
  programId: number
}

export const getProgramPurses = async ({
  programId,
}: GetProgramPursesParams) => {
  try {
    const response = await axios.get(`/programs/${programId}/purses`)
    return ProgramPurseListSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type GetPurseParams = {
  purseId: string
  programId: number
}
export const getPurseById = async ({ purseId, programId }: GetPurseParams) => {
  const response = await axios.get(`/programs/${programId}/purses/${purseId}`)
  return ProgramPurseResourceSchema.parse(response.data)
}

type PurseDistributionsParams = {
  programId: number
  purseId: number
}

export const getPurseDistributions = async (
  params: PurseDistributionsParams
) => {
  const { programId, purseId } = params
  const response = await axios.get(
    `/programs/${programId}/purses/${purseId}/list_distributions`
  )
  return GetPurseDistributionsResponseSchema.parse(response.data)
}

export type DownloadDistributionReportParams = {
  programId: number
  purseId: number
  distributionId: number
  password: string
}

export const downloadDistributionReport = async (
  params: DownloadDistributionReportParams
) => {
  const { programId, purseId, distributionId, password } = params
  const response = await axios.post(
    `/programs/${programId}/purses/${purseId}/distributions/${distributionId}/download_distribution_report`,
    { password },
    { responseType: 'blob' }
  )

  return response.data
}

export type DownloadDistributionPlanPreviewReportParams = {
  programId: number
  purseId: number
  distributionStrategy: 'single'
}

export async function downloadDistributionPlanPreviewReport({
  programId,
  purseId,
  distributionStrategy = 'single',
}: DownloadDistributionPlanPreviewReportParams) {
  const response = await axios.get(
    '/scholarships/distribution/download_distribution_preview_report',
    {
      params: {
        distribution_strategy: distributionStrategy,
        program_id: programId,
        program_purse_id: purseId,
      },
      responseType: 'blob',
    }
  )
  return response.data
}

export type RunPreviewDistributionParams = {
  programId: number
  purseId: number
  distributionStrategy?: 'single'
}

export const runPreviewDistribution = async ({
  programId,
  purseId,
  distributionStrategy = 'single',
}: RunPreviewDistributionParams): Promise<RunPreviewDistributionResponse> => {
  try {
    const response = await axios.post(
      '/scholarships/distribution/preview_distribution',
      {
        distribution_strategy: distributionStrategy,
        program_id: programId,
        program_purse_id: purseId,
      }
    )

    return RunPreviewDistributionResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

type PurseAffiliationsParams = {
  programId: number
  purseId: number
}

export const getPurseAffiliations = async (params: PurseAffiliationsParams) => {
  try {
    const { programId, purseId } = params
    const response = await axios.get(
      `/programs/${programId}/purses/${purseId}/settings/affiliations`
    )

    return ProgramPurseAffiliationsResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

type SyncPurseAffiliationsParams = PurseAffiliationsParams & {
  affiliationIds: number[]
}

export const syncPurseAffiliations = async (
  params: SyncPurseAffiliationsParams
) => {
  try {
    const { programId, purseId, affiliationIds } = params
    const response = await axios.post(
      `/programs/${programId}/purses/${purseId}/settings/affiliations`,
      {
        affiliation_ids: affiliationIds,
      }
    )

    return ProgramPurseAffiliationsResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type TransferPurseFundsParams = {
  programId: number
  purseId: number
  amount: number
  targetProgramPurseId: number
}

export const transferPurseFunds = async ({
  programId,
  purseId,
  amount,
  targetProgramPurseId,
}: TransferPurseFundsParams) => {
  try {
    const response = await axios.post(
      `/programs/${programId}/purses/${purseId}/transfer`,
      {
        amount,
        target_program_purse_id: targetProgramPurseId,
      }
    )

    return response.data
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type RemovePurseFundsParams = {
  programId: number
  purseId: number
  amount: number
}

export const removePurseFunds = async ({
  programId,
  purseId,
  amount,
}: RemovePurseFundsParams) => {
  try {
    const response = await axios.post(
      `/programs/${programId}/purses/${purseId}/remove_funds`,
      {
        amount,
      }
    )

    return response.data
  } catch (error) {
    throw parseRequestError(error)
  }
}
